import { type Mat3 } from '@/domain/math';

import { type Camera, type Viewport, worldToScreen } from './Camera';

/**
 * The three stacked canvases described in ADR-0004.
 *
 * | Layer     | Contents                                              | Repaints          |
 * | --------- | ----------------------------------------------------- | ----------------- |
 * | `grid`    | Grid lines and axes                                   | On camera change  |
 * | `content` | Scene entities                                        | Dirty region only |
 * | `overlay` | Handles, snap hints, guides, drag and tool previews    | Every frame       |
 *
 * The split is what keeps dragging cheap: the moving preview lives on `overlay`, which
 * holds a handful of primitives, so a drag frame costs a clear of a nearly-empty canvas
 * plus a small dirty-rect repaint of `content` — not a repaint of all 5 000 entities.
 */
export const LayerName = {
  Grid: 'grid',
  Content: 'content',
  Overlay: 'overlay',
} as const;
export type LayerName = (typeof LayerName)[keyof typeof LayerName];

export const LAYER_ORDER: readonly LayerName[] = Object.freeze([
  LayerName.Grid,
  LayerName.Content,
  LayerName.Overlay,
]);

export interface Layer {
  readonly name: LayerName;
  readonly canvas: HTMLCanvasElement;
  readonly context: CanvasRenderingContext2D;
}

export interface LayerStackOptions {
  /**
   * Overrides `window.devicePixelRatio`. Present so tests can pin a ratio; production
   * code should leave it unset and let the stack track the real display.
   *
   * Explicitly `| undefined` so that callers can forward an absent option straight
   * through under `exactOptionalPropertyTypes`, rather than each of them rebuilding the
   * object conditionally.
   */
  readonly devicePixelRatio?: number | undefined;
}

/**
 * Owns the canvas elements and everything about pixel geometry.
 *
 * HiDPI is handled here and **only** here: the backing store is sized in device pixels
 * while every painter works in CSS pixels. Leaking the ratio into painters is how canvas
 * editors end up with hairlines that are crisp on one display and blurry on another.
 */
export class LayerStack {
  private readonly layers = new Map<LayerName, Layer>();
  private viewportWidth = 0;
  private viewportHeight = 0;
  private ratio = 1;
  private readonly ratioOverride: number | undefined;
  private readonly container: HTMLElement;

  constructor(container: HTMLElement, options: LayerStackOptions = {}) {
    this.container = container;
    this.ratioOverride = options.devicePixelRatio;

    for (const name of LAYER_ORDER) {
      const canvas = container.ownerDocument.createElement('canvas');
      canvas.dataset['layer'] = name;
      canvas.style.position = 'absolute';
      canvas.style.inset = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      // Pointer events are handled once, on the container, so the layers never
      // interfere with hit routing.
      canvas.style.pointerEvents = 'none';

      const context = canvas.getContext('2d');
      if (context === null) {
        throw new Error(`EduSketch: 2D context unavailable for the "${name}" layer`);
      }

      container.appendChild(canvas);
      this.layers.set(name, { name, canvas, context });
    }
  }

  get(name: LayerName): Layer {
    const layer = this.layers.get(name);
    if (layer === undefined) throw new Error(`EduSketch: unknown layer "${name}"`);
    return layer;
  }

  get viewport(): Viewport {
    return { width: this.viewportWidth, height: this.viewportHeight };
  }

  get devicePixelRatio(): number {
    return this.ratio;
  }

  /**
   * Resizes the backing stores.
   *
   * Returns `true` only when something actually changed, so the caller can skip a
   * repaint. Resizing a canvas clears it, which makes an unnecessary resize both wasteful
   * and visibly wrong — a no-op check here is not micro-optimisation.
   */
  resize(width: number, height: number): boolean {
    const ratio = this.ratioOverride ?? globalThis.devicePixelRatio;
    const nextRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;

    if (
      width === this.viewportWidth &&
      height === this.viewportHeight &&
      nextRatio === this.ratio
    ) {
      return false;
    }

    this.viewportWidth = width;
    this.viewportHeight = height;
    this.ratio = nextRatio;

    const deviceWidth = Math.max(1, Math.round(width * nextRatio));
    const deviceHeight = Math.max(1, Math.round(height * nextRatio));

    for (const layer of this.layers.values()) {
      layer.canvas.width = deviceWidth;
      layer.canvas.height = deviceHeight;
    }
    return true;
  }

  /**
   * Applies the device-pixel scale and the camera transform to a layer's context.
   *
   * After this call the context is in **world** coordinates: a painter draws at
   * `(3, 4)` and the point lands where world `(3, 4)` belongs, at the right density.
   */
  applyCamera(name: LayerName, cameraState: Camera): Mat3 {
    const { context } = this.get(name);
    const m = worldToScreen(cameraState, this.viewport);
    context.setTransform(
      m.a * this.ratio,
      m.b * this.ratio,
      m.c * this.ratio,
      m.d * this.ratio,
      m.e * this.ratio,
      m.f * this.ratio
    );
    return m;
  }

  /** Resets a layer's context to raw device pixels, for painting screen-space chrome. */
  applyIdentity(name: LayerName): void {
    this.get(name).context.setTransform(this.ratio, 0, 0, this.ratio, 0, 0);
  }

  /** Clears a whole layer, ignoring any transform currently set on it. */
  clear(name: LayerName): void {
    const { canvas, context } = this.get(name);
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.restore();
  }

  /** Removes the canvases from the DOM. */
  dispose(): void {
    for (const layer of this.layers.values()) {
      layer.canvas.remove();
    }
    this.layers.clear();
    this.container.replaceChildren();
  }
}
